import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionItem,
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Switch,
  Tab,
  Tabs,
} from "@heroui/react";
import { api, type CatalogTool } from "../lib/api.ts";
import { useLab, useRun } from "../lib/state.tsx";
import { Json } from "../components/Json.tsx";
import { Section } from "../components/Section.tsx";

interface Primitives {
  resources: Array<{ uri: string; name: string; title: string; description: string; mimeType: string }>;
  resourceTemplates: Array<{ uriTemplate: string; name: string; title: string; description: string }>;
  prompts: Array<{ name: string; title: string; description: string; arguments: Array<{ name: string; description: string; required?: boolean }> }>;
  deprecated: {
    sep: string;
    deprecatedIn: string;
    features: Record<string, string>;
  };
}

const GROUP_COLOUR: Record<string, "primary" | "secondary" | "warning" | "danger"> = {
  core: "primary",
  meta: "secondary",
  corp: "warning",
  demo: "danger",
};

export function PrimitivesPanel() {
  const { modes, setMode } = useLab();
  const [primitives, setPrimitives] = useState<Primitives | null>(null);
  const [tools, setTools] = useState<CatalogTool[]>([]);
  const run = useRun<{ kind: string; result?: unknown; error?: unknown }>();
  const deprecated = useRun<{ which: string; serverRequests: unknown[]; result: unknown }>();

  const legacy = modes?.modes.legacy ?? false;

  useEffect(() => {
    void api.primitives().then(setPrimitives);
    void api.catalog({ hidden: modes?.modes.poison }).then((c) => setTools(c.tools));
  }, [modes?.modes.poison]);

  return (
    <div className="flex flex-col gap-4">
      <Section
        title="Six primitives, four of them still recommended"
        blurb="Tools, resources and prompts are the server's side. Elicitation is how it asks the user something. Sampling and roots were the two that pointed the other way — and 2026-07-28 deprecated both, along with logging."
      >
        <Tabs aria-label="Primitives" variant="solid" size="sm">
          <Tab key="tools" title={`Tools (${tools.length})`}>
            <Accordion isCompact selectionMode="multiple" className="px-0">
              {tools.slice(0, 60).map((t) => (
                <AccordionItem
                  key={t.name}
                  aria-label={t.name}
                  title={
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs">{t.name}</span>
                      <Chip size="sm" variant="flat" color={GROUP_COLOUR[t.group]} className="h-5 text-[10px]">
                        {t.group}
                      </Chip>
                      {t.annotations?.readOnlyHint === false && (
                        <Chip size="sm" variant="flat" color="danger" className="h-5 text-[10px]">
                          writes
                        </Chip>
                      )}
                      {t.scopes.map((s) => (
                        <Chip key={s} size="sm" variant="bordered" className="h-5 font-mono text-[10px]">
                          {s}
                        </Chip>
                      ))}
                    </div>
                  }
                >
                  <p className="mb-2 text-xs whitespace-pre-wrap text-default-400">{t.description}</p>
                  <Json value={t.inputSchema} className="max-h-72 overflow-y-auto" />
                  <div className="mt-2 flex items-center gap-2">
                    <span className="font-mono text-[10px] text-default-600">sha256:{t.hash}</span>
                    <Button
                      size="sm"
                      variant="flat"
                      onPress={() =>
                        void run.run("primitive", {
                          tool: "tools/call",
                          args: { name: t.name, arguments: {} },
                        })
                      }
                    >
                      Call with no arguments
                    </Button>
                  </div>
                </AccordionItem>
              ))}
            </Accordion>
            {tools.length > 60 && (
              <p className="px-2 pt-2 text-xs text-default-500">
                Showing the first 60 of {tools.length}. The rest are in the catalogue; §5 is where
                the size starts to matter.
              </p>
            )}
          </Tab>

          <Tab key="resources" title={`Resources (${primitives?.resources.length ?? 0})`}>
            <div className="flex flex-col gap-2 pt-2">
              {primitives?.resources.map((r) => (
                <div
                  key={r.uri}
                  className="flex flex-wrap items-center gap-2 rounded-medium border border-default-100/60 px-3 py-2"
                >
                  <span className="font-mono text-xs text-primary">{r.uri}</span>
                  <span className="text-xs text-default-400">{r.title}</span>
                  <Button
                    size="sm"
                    variant="flat"
                    className="ml-auto"
                    onPress={() => void run.run("primitive", { tool: "resources/read", args: { uri: r.uri } })}
                  >
                    Read
                  </Button>
                </div>
              ))}
              <p className="pt-2 text-xs text-default-500">
                Templates: {primitives?.resourceTemplates.map((t) => t.uriTemplate).join(", ")}
              </p>
            </div>
          </Tab>

          <Tab key="prompts" title={`Prompts (${primitives?.prompts.length ?? 0})`}>
            <div className="flex flex-col gap-2 pt-2">
              {primitives?.prompts.map((p) => (
                <div key={p.name} className="rounded-medium border border-default-100/60 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-primary">{p.name}</span>
                    <Button
                      size="sm"
                      variant="flat"
                      className="ml-auto"
                      onPress={() => void run.run("primitive", { tool: "prompts/get", args: { name: p.name } })}
                    >
                      Get
                    </Button>
                  </div>
                  <p className="mt-1 text-xs text-default-400">{p.description}</p>
                  <p className="mt-1 font-mono text-[10px] text-default-600">
                    args: {p.arguments.map((a) => `${a.name}${a.required ? "*" : ""}`).join(", ") || "none"}
                  </p>
                </div>
              ))}
            </div>
          </Tab>

          <Tab key="elicitation" title="Elicitation">
            <div className="pt-2">
              <p className="mb-3 max-w-2xl text-xs text-default-400">
                Still supported, but no longer a server-initiated request. Under MRTR the server
                returns <code className="text-warning">resultType: "input_required"</code> and the
                client retries. The full round trip is on the §3 tab.
              </p>
              <Button
                size="sm"
                color="primary"
                onPress={() =>
                  void run.run("primitive", {
                    tool: "tools/call",
                    args: { name: "deploy_note", arguments: { slug: "adr-007-deployment-environments" } },
                  })
                }
              >
                Trigger an elicitation
              </Button>
            </div>
          </Tab>
        </Tabs>

        {(run.data || run.error) && (
          <Card shadow="none" className="mt-3 border border-default-100/60 bg-default-50/30">
            <CardHeader className="pb-1 font-mono text-xs">{run.data?.kind ?? "error"}</CardHeader>
            <CardBody className="pt-0">
              <Json value={run.error ?? run.data?.error ?? run.data?.result} className="max-h-72 overflow-y-auto" />
            </CardBody>
          </Card>
        )}
      </Section>

      <Section
        title="The two that were lost"
        blurb="Sampling let a server borrow the client's model. Roots let it ask what directories it could see. Both pointed server → client, both are deprecated as of 2026-07-28, and both only work here over the legacy adapter."
        tone={legacy ? "danger" : "default"}
      >
        <span data-testid="legacy-switch" data-on={legacy ? "1" : "0"}>
          <Switch
            size="sm"
            aria-label="reveal deprecated surface"
            isSelected={legacy}
            onValueChange={(v) => void setMode("legacy", v)}
            color="danger"
          >
            <span className="text-xs">Reveal the deprecated surface (2025-06-18 only)</span>
          </Switch>
        </span>

        {legacy && primitives && (
          <div className="mt-4 flex flex-col gap-3">
            {(["sampling", "roots", "logging"] as const).map((feature) => (
              <div key={feature} className="rounded-medium border border-danger/40 bg-danger/5 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm text-danger">{feature}</span>
                  <Chip size="sm" color="danger" variant="flat" className="h-5 font-mono text-[10px]">
                    deprecated {primitives.deprecated.deprecatedIn} · {primitives.deprecated.sep}
                  </Chip>
                  {feature !== "logging" && (
                    <Button
                      size="sm"
                      color="danger"
                      variant="flat"
                      className="ml-auto"
                      isLoading={deprecated.pending}
                      onPress={() => void deprecated.run("legacy-deprecated", { tool: feature })}
                    >
                      Run it over 2025-06-18
                    </Button>
                  )}
                </div>
                <p className="mt-2 text-xs text-default-300">
                  {primitives.deprecated.features[feature]}
                </p>
              </div>
            ))}

            {deprecated.data && (
              <Card shadow="none" className="border border-default-100/60 bg-default-50/30">
                <CardHeader className="pb-1 text-xs font-medium">
                  The server sent {deprecated.data.serverRequests.length} request(s) down the SSE
                  stream — a shape that no longer exists
                </CardHeader>
                <CardBody className="pt-0">
                  <Json value={deprecated.data.serverRequests} className="max-h-52 overflow-y-auto" />
                  <p className="mt-2 mb-1 text-xs text-default-500">…and the tool call resolved with:</p>
                  <Json value={deprecated.data.result} className="max-h-52 overflow-y-auto" />
                </CardBody>
              </Card>
            )}
          </div>
        )}
      </Section>
    </div>
  );
}
